import React, { useState } from "react";
import { Select, DatePicker, Space, Input, Row, Col } from "antd";
import { startCase } from "lodash";
import {
  USER_BADGES,
  USER_STATUS,
  USER_TAGGING,
} from "../../constants/user";
import { TOP_UP_PACKAGES } from "../../constants/topupPackages";

const UserFilters = ({ filters, onFiltersChange, tabName, accManager, availableCourierPackages }) => {
  const [searchField, setSearchField] = useState("email");

  const isDangerUserTab = tabName === "dangerUser";
  const dateFilterLabel = isDangerUserTab
    ? "RTO Date Range"
    : "Email Verified On";

  const searchBy = (
    <Select
      defaultValue="email"
      className="select-before"
      onChange={(value) => setSearchField(value)}
    >
      <Select.Option value="email">User Email</Select.Option>
      <Select.Option value="masterEmail">Master Email</Select.Option>
      <Select.Option value="username">Username</Select.Option>
      <Select.Option value="phone">Phone</Select.Option>
      <Select.Option value="userId">User Id</Select.Option>
      <Select.Option value="merchantId">Merchant Id</Select.Option>
    </Select>
  );

  const renderSearch = () => (
    <div>
      Search :
      <Input.Search
        addonBefore={searchBy}
        placeholder="Search"
        value={filters.search}
        onChange={(e) =>
          onFiltersChange({ ...filters, search: e.target.value, searchField })
        }
        onSearch={(term) =>
          onFiltersChange({ ...filters, search: term, searchField })
        }
      />
    </div>
  );

  const renderMainDateFilter = () => (
    <div>
      {dateFilterLabel} :
      <DatePicker.RangePicker
        value={[filters.startDate, filters.endDate]}
        style={{ width: "100%" }}
        onChange={(dates) =>
          dates
            ? onFiltersChange({
              ...filters,
              startDate: dates[0],
              endDate: dates[1],
            })
            : onFiltersChange({
              ...filters,
              startDate: undefined,
              endDate: undefined,
            })
        }
      />
    </div>
  );

  const renderLastOrderDate = () => (
    <div>
      Last Order Date :
      <DatePicker.RangePicker
        style={{ width: "100%" }}
        value={[filters.startLastOrderDate, filters.endLastOrderDate]}
        onChange={(dates) =>
          dates
            ? onFiltersChange({
              ...filters,
              startLastOrderDate: dates[0],
              endLastOrderDate: dates[1],
            })
            : onFiltersChange({
              ...filters,
              startLastOrderDate: undefined,
              endLastOrderDate: undefined,
            })
        }
      />
    </div>
  );

  const renderLastActiveDate = () => (
    <div>
      Last Active Date :
      <DatePicker.RangePicker
        style={{ width: "100%" }}
        value={[filters.startLastActiveDate, filters.endLastActiveDate]}
        onChange={(dates) =>
          dates
            ? onFiltersChange({
              ...filters,
              startLastActiveDate: dates[0],
              endLastActiveDate: dates[1],
            })
            : onFiltersChange({
              ...filters,
              startLastActiveDate: undefined,
              endLastActiveDate: undefined,
            })
        }
      />
    </div>
  );

  const renderLastTopupDate = () => (
    <div>
      Last Topup Date :
      <DatePicker.RangePicker
        style={{ width: "100%" }}
        value={[filters.topupStartDate, filters.topupEndDate]}
        onChange={(dates) =>
          dates
            ? onFiltersChange({
              ...filters,
              topupStartDate: dates[0],
              topupEndDate: dates[1],
            })
            : onFiltersChange({
              ...filters,
              topupStartDate: undefined,
              topupEndDate: undefined,
            })
        }
      />
    </div>
  );

  const renderBadge = () => (
    <div>
      Badge :
      <Select
        placeholder="Select"
        style={{ width: "100%" }}
        allowClear
        value={filters.badge}
        onChange={(value) => onFiltersChange({ ...filters, badge: value })}
      >
        {Object.entries(USER_BADGES).map(([key, value]) => (
          <Select.Option key={key} value={key}>
            {value}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  const renderStatus = () => (
    <div>
      Status :
      <Select
        placeholder="Select"
        style={{ width: "100%" }}
        allowClear
        value={filters.status}
        onChange={(value) => onFiltersChange({ ...filters, status: value })}
      >
        {Object.entries(USER_STATUS).map(([key, value]) => (
          <Select.Option key={key} value={key}>
            {value}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  const renderPackage = () => (
    <div>
      Package :
      <Select
        placeholder="Select"
        style={{ width: "100%" }}
        allowClear
        value={filters.topupPackage}
        onChange={(value) =>
          onFiltersChange({ ...filters, topupPackage: value })
        }
      >
        {Object.entries(TOP_UP_PACKAGES).map(([key, pkg]) => (
          <Select.Option key={key} value={key}>
            {pkg.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  const renderCodPackage = () => (
    <div>
      COD Package :
      <Select
        placeholder="Select"
        style={{ width: "100%" }}
        allowClear
        value={filters.courierPackage}
        onChange={(value) =>
          onFiltersChange({ ...filters, courierPackage: value })
        }
      >
        {availableCourierPackages.map((pkg) => (
          <Select.Option key={pkg} value={pkg}>
            {startCase(pkg)}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  const renderTagging = () => (
    <div>
      Tagging :
      <Select
        placeholder="Select"
        style={{ width: "100%" }}
        allowClear
        value={filters.tagging}
        onChange={(value) => onFiltersChange({ ...filters, tagging: value })}
      >
        {Object.entries(USER_TAGGING).map(([key, value]) => (
          <Select.Option key={key} value={key}>
            {value}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  const renderCompleteSignup = () => (
    <div>
      Complete Signup:
      <Select
        placeholder="Select"
        style={{ width: "100%" }}
        allowClear
        value={filters.hasMerchant}
        onChange={(value) =>
          onFiltersChange({ ...filters, hasMerchant: value })
        }
      >
        <Select.Option key={1} value={true}>
          Yes
        </Select.Option>
        <Select.Option key={0} value={false}>
          No
        </Select.Option>
        <Select.Option key={"all"} value={null}>
          All
        </Select.Option>
      </Select>
    </div>
  );

  const renderAccountManager = () => (
    <div>
      Account Manager :
      <Select
        mode="multiple"
        allowClear
        style={{ width: "100%" }}
        placeholder="Select account manager"
        value={filters.accountManagerId}
        onChange={(value) =>
          onFiltersChange({ ...filters, accountManagerId: value })
        }
      >
        {accManager?.map((manager) => (
          <Select.Option key={manager.id} value={manager.id}>
            {manager.email}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  if (isDangerUserTab) {
    return (
      <Space size="large" direction="vertical" style={{ width: "100%" }} wrap>
        <Row gutter={16}>
          <Col xs={24} md={12} lg={8}>
            {renderMainDateFilter()}
          </Col>
          <Col xs={24} md={24} lg={8}>
            {renderSearch()}
          </Col>
        </Row>
      </Space>
    );
  }

  return (
    <Space size="large" direction="vertical" style={{ width: "100%" }} wrap>
      <Row gutter={16}>
        <Col xs={24} md={12} lg={6}>
          {renderMainDateFilter()}
        </Col>
        <Col xs={24} md={12} lg={6}>
          {renderLastActiveDate()}
        </Col>
        <Col xs={24} md={12} lg={6}>
          {renderLastTopupDate()}
        </Col>
        <Col xs={24} md={12} lg={6}>
          {renderLastOrderDate()}
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12} lg={6}>
          {renderSearch()}
        </Col>
        <Col xs={24} md={12} lg={6}>
          {renderBadge()}
        </Col>
        <Col xs={24} md={12} lg={6}>
          {renderStatus()}
        </Col>
        <Col xs={24} md={12} lg={6}>
          {renderPackage()}
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12} lg={6}>
          {renderCodPackage()}
        </Col>
        
        <Col xs={24} md={12} lg={6}>
          {renderTagging()}
        </Col>

        <Col xs={24} md={12} lg={6}>
          {renderCompleteSignup()}
        </Col>

        <Col xs={24} md={12} lg={6}>
          {renderAccountManager()}
        </Col>
      </Row>
    </Space>
  );
};

export default UserFilters;